import { Files, Flag, Warning } from '@phosphor-icons/react';

import { Eye } from '@phosphor-icons/react';
import { CheckCircle, CheckSquare, Empty, Square } from '@phosphor-icons/react';
import { MonthlyDeclarationOverviewItem } from '../../../@types/TaxProfile';
import { getMonthName } from '../../../shared/utils/dates.util';
import {
	capitalizeFirstLetter,
	formatCurrency,
} from '../../../shared/utils/general.util';
import { DeclarationOverviewStatus } from './DeclarationsOverview';

const DeclarationOverviewItem = ({
	declaration,
	index,
	array,
	selectedDeclarations,
	setSelectedDeclarations,
	setSelectedPresentedDeclaration,
	variant = 'default',
	showIncomes = true,
}: {
	declaration: MonthlyDeclarationOverviewItem;
	index: number;
	array: MonthlyDeclarationOverviewItem[];
	selectedDeclarations: MonthlyDeclarationOverviewItem[];
	setSelectedDeclarations: (
		declarations: MonthlyDeclarationOverviewItem[],
	) => void;
	setSelectedPresentedDeclaration: (
		declaration: MonthlyDeclarationOverviewItem | null,
	) => void;
	variant?: 'default' | 'condensed';
	showIncomes?: boolean;
}) => {
	const isPresented =
		declaration?.obligation_compliance_check ===
		DeclarationOverviewStatus.PRESENTED;
	const isPending =
		declaration?.obligation_compliance_check ===
		DeclarationOverviewStatus.MISSING;
	const isAcquired =
		declaration?.period_hired_with_heru === 'Periodo contratado' &&
		!isPresented;

	const isLastItem = index === array.length - 1;
	const isSelected = selectedDeclarations.includes(declaration);

	const daysLate = () => {
		if (isPresented) return 0;

		// For annual declarations, use May 1st of the declaration year as deadline
		if (declaration.type === 'annual') {
			// Get the year from the declaration
			const year = Number(declaration.declaration_year);

			// Set deadline to May 1st of that year
			const deadline = new Date(year, 4, 1); // Month is 0-indexed, so 4 = May

			// Get current date
			const today = new Date();

			// If deadline is in the future, return 0
			if (today <= deadline) return 0;

			// Calculate difference in days
			const diffTime = today.getTime() - deadline.getTime();
			const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

			return diffDays;
		}

		// For non-annual declarations, use existing logic
		// Extract the month and year from the declaration period
		const [month, year] = declaration.declaration_period
			?.split('-')
			.map(Number) || [0, 0];

		// Calculate the deadline (17th of the following month)
		const deadlineMonth = month === 12 ? 1 : month + 1;
		const deadlineYear = month === 12 ? year + 1 : year;
		const deadline = new Date(deadlineYear, deadlineMonth - 1, 17);

		// Get current date
		const today = new Date();

		// If deadline is in the future, return 0
		if (today <= deadline) return 0;

		// Calculate difference in days
		const diffTime = today.getTime() - deadline.getTime();
		const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

		return diffDays;
	};

	const toggleSelectedDeclaration = () => {
		if (selectedDeclarations.includes(declaration)) {
			setSelectedDeclarations(
				selectedDeclarations.filter((item) => item !== declaration),
			);
		} else {
			setSelectedDeclarations([...selectedDeclarations, declaration]);
		}
	};

	const regime = declaration?.type
		? {
				platec: 'Plataformas tecnológicas',
				resico: 'RESICO',
				annual: 'Declaración anual',
			}[declaration?.type]
		: 'Plataformas tecnológicas';

	const shortRegime = declaration?.type
		? {
				platec: 'Plataformas Tec.',
				resico: 'RESICO',
				annual: 'Anual',
			}[declaration?.type]
		: 'Plataformas T.';

	const title =
		declaration?.type === 'annual'
			? `Declaración anual ${declaration?.declaration_year}`
			: `${capitalizeFirstLetter(
					getMonthName(Number(declaration.declaration_period?.split('-')[0])),
				)} ${declaration.declaration_period?.split('-')[1]}`;

	const labelTag = isPresented
		? 'Presentada'
		: isAcquired
			? 'Contratado'
			: declaration?.type === 'annual'
				? `Atrasada ${daysLate()} días`
				: `Atrasada ${daysLate()} días`;

	if (variant === 'condensed') {
		return (
			<button
				style={{
					animation: 'fadeInScale 0.100s ease forwards',
					animationDelay: `${index * 20}ms`,
					opacity: 0,
				}}
				onClick={(e) => {
					e.stopPropagation();
					if (isPresented) {
						setSelectedPresentedDeclaration(declaration);
					} else if (!isAcquired) {
						toggleSelectedDeclaration();
					}
				}}
				key={`${declaration.declaration_period}-${declaration.obligation_compliance_check}-${index}`}
				className={`text-left leading-tight w-full flex flex-col p-1 rounded-lg  border border-neutral-400/70 items-start
				${isSelected ? 'bg-h-primary-95/70 !border-h-primary-70' : 'hover:bg-neutral-50'}
				${isAcquired ? 'cursor-not-allowed hover:!bg-white border-h-primary-30' : ''}
				`}
			>
				<div className='text-sm flex items-start justify-between w-full mb-1'>
					<p>{title?.split(' ')[0]}</p>
				</div>

				<p className='flex gap-1 text-neutral-500 text-xs'>{shortRegime}</p>
				<p className='flex gap-1 text-neutral-500 text-xs'>
					{formatCurrency(declaration?.identified_income ?? 0)}
				</p>
				<div className='text-xs rounded-full bg-red-100 text-red-800 px-1 mt-1 hidden'>
					Pendiente
				</div>
			</button>
		);
	}

	return (
		<>
			<button
				style={{
					animation: 'fadeInScale 0.150s ease forwards',
					animationDelay: `${index * 40}ms`,
					opacity: 0,
				}}
				onClick={() => {
					if (isPresented) {
						setSelectedPresentedDeclaration(declaration);
					} else if (!isAcquired) {
						toggleSelectedDeclaration();
					}
				}}
				key={`${declaration.declaration_period}-${declaration.obligation_compliance_check}-${index}`}
				className={`w-full flex flex-col p-2 md:p-1 rounded-lg hover:bg-neutral-50
					${isSelected ? 'bg-neutral-50' : ''}
					${isAcquired && !isPresented ? 'cursor-not-allowed hover:!bg-white' : ''}
					`}
			>
				<div className='flex items-start justify-between w-full'>
					<div className='flex flex-col gap-1 items-start'>
						<p className='font-medium leading-tight'>{title}</p>
					</div>
					{isPresented ? (
						<p className='flex items-center gap-1 text-green-800 text-sm'>
							<CheckCircle
								size={20}
								weight='duotone'
							/>
						</p>
					) : (
						<p
							className={`flex items-center gap-1 text-sm 

								${isSelected ? 'text-green-800' : ''}
								
								${isAcquired ? 'text-green-800' : ''}`}
						>
							{isAcquired ? 'Contratado' : 'Contratar'}
							{isSelected || isAcquired ? (
								<CheckSquare
									size={20}
									weight='fill'
								/>
							) : (
								<Square size={20} />
							)}
						</p>
					)}
				</div>

				<div className='flex flex-col md:flex-row md:gap-3 tracking-tight text-sm mt-2 md:mt-0'>
					{declaration?.type !== 'annual' && (
						<p className='flex items-center gap-1 text-neutral-500'>
							{isPresented ? (
								<Files weight='bold' />
							) : isAcquired ? (
								<CheckCircle weight='bold' />
							) : (
								<Empty weight='bold' />
							)}
							{isPresented
								? `Declaración ${declaration?.declaration_type?.toLowerCase()}`
								: isAcquired
									? 'Periodo contratado'
									: 'Periodo no contratado'}
						</p>
					)}

					<p className='flex items-center gap-1 text-neutral-500'>
						<Flag weight='bold' />
						{regime}
					</p>
				</div>
				<div className='flex items-end w-full gap-2 justify-between mt-3 text-sm tracking-tight'>
					{(isPresented || (isPending && daysLate() > 0) || isAcquired) && (
						<div
							className={`text-sm w-fit px-3 py-1 rounded-full flex items-center gap-2 ${
								isPresented
									? 'bg-green-100 text-green-800'
									: isAcquired
										? 'border border-green-800 text-green-800'
										: 'bg-red-100 text-red-800'
							}`}
						>
							{isPresented && (
								<Eye
									size={16}
									weight='bold'
								/>
							)}

							{isPending && !isAcquired && (
								<Warning
									size={16}
									weight='bold'
								/>
							)}

							{labelTag}
						</div>
					)}
					{showIncomes && declaration?.identified_income! > 0 && (
						<div className='flex items-end md:items-center md:gap-2 text-sm flex-col md:flex-row '>
							<p className='text-neutral-500'>
								{declaration?.type === 'annual'
									? 'Ingreso anual'
									: 'Ingresos del mes'}
							</p>
							<p className='font-medium'>
								{formatCurrency(declaration?.identified_income ?? 0)}
							</p>
						</div>
					)}
				</div>
			</button>
			{!isLastItem && <hr className='opacity-70 !m-0' />}
		</>
	);
};

export default DeclarationOverviewItem;
